import React, { useEffect, useState } from 'react';
import { permissionApi } from '../../services/api';
import { getStepUpChallengeFromError } from '../../utils/stepUp';
import StepUpDialog from '../auth/StepUpDialog';
import { ORGANISATION_PERMISSION_KEYS, formatPermissionLabel } from './organisationPermissionKeys';

// D.4: "starting with the four [pricing keys], extensible to any
// OrganisationPermissionKey" — this MR ships the default list scoped to the
// four pricing keys the owner's ruling was about; any future caller can
// widen it to the full `ORGANISATION_PERMISSION_KEYS` (already imported
// above, so the two editors can never define the key set twice) via the
// `keys` prop without touching this file again.
const DEFAULT_MEMBER_PERMISSION_OVERRIDE_KEYS = [
  'pricing_plans_view',
  'pricing_plans_manage',
  'pricing_member_plan_assign',
  'pricing_booking_reprice',
].filter((key) => ORGANISATION_PERMISSION_KEYS.includes(key));

// Mantis #1390 / M004-F0618 §D.4 — the per-person permission override panel.
// Genuinely missing before this MR: `permissionPolicyService
// .updateUserOrganisationPermissions` (Core, M001-F0227) had no HTTP surface
// and the WebUI had no per-person permission UI at all — only the per-role
// Role Management tab existed. Core MR !571 (#1385) closes the API gap with
// `GET`/`PUT /v1/admin/organisations/:organisationId/users/:userId/permissions`
// (`permissions_system_manage` + SAL-4 purpose `permissions.organisation.manage`
// — verified against `admin.routes.ts` on that branch, 2026-09-06); this panel
// is that route's only caller.
//
// Tri-state per key, mirroring `permissions_user_organisation`'s own grain
// (NULL = inherit from the role/system default, true = allow, false = deny):
// a checkbox is the primary control (checked = explicit allow, unchecked =
// explicit deny) because that is the control shape T-M004-0025 cases C/D
// exercise (click toggles allow <-> deny; a separate "Use role default"
// action reaches the third, inherit, state without overloading one click
// target with three meanings).
//
// SAL-4 step-up: identical pattern to `submitChangePlan`/
// `completeChangePlanStepUp` elsewhere in AdminComponents.js — a 403 challenge
// pauses the save, `StepUpDialog` completes it, and the SAME save is retried
// verbatim. No new step-up flow is invented here.
const MemberPermissionOverridePanel = ({
  organisationId,
  orgUser,
  keys = DEFAULT_MEMBER_PERMISSION_OVERRIDE_KEYS,
  onClose,
  onSaved,
}) => {
  const [loaded, setLoaded] = useState(null); // raw tri-state from the server: Record<key, boolean|null>
  const [pending, setPending] = useState({}); // only keys the admin touched this session
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [stepUpChallenge, setStepUpChallenge] = useState(null);
  const [stepUpRetry, setStepUpRetry] = useState(null);

  const userId = orgUser?.uuid || orgUser?.user_uuid || orgUser?.id;
  const memberLabel = orgUser
    ? (orgUser.display_name || orgUser.displayName || orgUser.primary_email || orgUser.email || `User #${orgUser.id}`)
    : '';

  const getApiErrorMessage = (err, fallback) => (
    err.response?.data?.message
    || err.response?.data?.error?.message
    || err.response?.data?.error
    || err.response?.data?.code
    || err.message
    || fallback
  );

  const loadPermissions = async () => {
    if (!organisationId || !userId) return;
    setLoading(true);
    setLoadError('');
    try {
      const response = await permissionApi.getUserPermissions(organisationId, userId);
      // Real shape (verified against `permissionPolicy.service.ts`'s
      // `getUserOrganisationPermissions` on Core MR !571): the tri-state map
      // is nested one level under `permission.permissions`, not on
      // `permission` itself — `permission` also carries
      // userId/organisationId/updatedByUserId/createdAt/updatedAt siblings.
      const permission = response.data?.data?.permission?.permissions || {};
      setLoaded(permission);
      setPending({});
    } catch (err) {
      const challenge = getStepUpChallengeFromError(err);
      if (challenge?.id) {
        setStepUpChallenge(challenge);
        setStepUpRetry(() => loadPermissions);
        return;
      }
      setLoaded(null);
      setLoadError(getApiErrorMessage(err, 'Could not load this member’s permission overrides.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPermissions();
  }, [organisationId, userId]);

  // Fails closed: a key absent from `loaded` (load error, or not yet
  // fetched) never renders as granted.
  const displayedValue = (key) => {
    if (Object.prototype.hasOwnProperty.call(pending, key)) return pending[key];
    if (!loaded) return null;
    const value = loaded[key];
    return value === true ? true : value === false ? false : null;
  };

  const toggleKey = (key) => {
    const current = displayedValue(key);
    const next = current === true ? false : true;
    setSaveStatus('');
    setPending((prevPending) => ({ ...prevPending, [key]: next }));
  };

  const resetToDefault = (key) => {
    setSaveStatus('');
    setPending((prevPending) => ({ ...prevPending, [key]: null }));
  };

  const pendingCount = Object.keys(pending).length;
  const canSave = pendingCount > 0 && reason.trim().length >= 3 && !saving;

  const savePermissions = async () => {
    if (!canSave || !organisationId || !userId) return;
    setSaving(true);
    setSaveError('');
    setSaveStatus('');
    try {
      await permissionApi.updateUserPermissions(organisationId, userId, {
        reason: reason.trim(),
        permissions: pending,
      });
      setStepUpChallenge(null);
      setStepUpRetry(null);
      await loadPermissions();
      setReason('');
      setSaveStatus('Permission overrides saved and reloaded from the live policy.');
      onSaved?.();
    } catch (err) {
      const challenge = getStepUpChallengeFromError(err);
      if (challenge?.id) {
        setStepUpChallenge(challenge);
        setStepUpRetry(() => savePermissions);
        setSaveError('A fresh 2SV step-up is required before this permission change can continue.');
        return;
      }
      setSaveError(getApiErrorMessage(err, 'Failed to save permission overrides.'));
    } finally {
      setSaving(false);
    }
  };

  const completeStepUp = async () => {
    const retry = stepUpRetry;
    setStepUpChallenge(null);
    setStepUpRetry(null);
    if (retry) await retry();
  };

  const cancelStepUp = () => {
    setStepUpChallenge(null);
    setStepUpRetry(null);
  };

  if (!orgUser) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal modal-md member-permission-override-panel"
        data-testid="member-permission-override-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>Permission overrides for {memberLabel}</h2>
          <button type="button" className="modal-close" aria-label="Close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <p className="text-muted">
            Per-person overrides win over the organisation&apos;s role default, which wins over the
            platform default. &quot;Use role default&quot; clears the override for a key so it inherits
            again.
          </p>
          {loading ? (
            <div className="loading-spinner">Loading permission overrides...</div>
          ) : loadError ? (
            <div className="role-management-notice" data-testid="member-permission-override-load-error">
              <strong>Could not load</strong>
              <span>{loadError}</span>
            </div>
          ) : (
            <table className="permission-matrix" data-testid="member-permission-override-table">
              <thead>
                <tr>
                  <th scope="col">Key</th>
                  <th scope="col">Granted</th>
                  <th scope="col">Default</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((key) => {
                  const value = displayedValue(key);
                  return (
                    <tr key={key}>
                      <th scope="row">{formatPermissionLabel(key)}</th>
                      <td>
                        <label className="permission-toggle" title={formatPermissionLabel(key)}>
                          <input
                            type="checkbox"
                            checked={value === true}
                            onChange={() => toggleKey(key)}
                            data-testid={`member-permission-override-${key}`}
                          />
                          <span>
                            {value === true ? 'Allowed' : value === false ? 'Denied' : 'Inherit (role default)'}
                          </span>
                        </label>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-link btn-sm"
                          data-testid={`member-permission-override-${key}-reset-default`}
                          onClick={() => resetToDefault(key)}
                          disabled={value === null}
                        >
                          Use role default
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {saveError && (
            <div className="role-management-notice" data-testid="member-permission-override-error">
              <strong>Save failed</strong>
              <span>{saveError}</span>
            </div>
          )}
          {saveStatus && (
            <div className="role-management-status" data-testid="member-permission-override-status">
              {saveStatus}
            </div>
          )}
          <label className="permission-organisation-field" htmlFor="memberPermissionOverrideReason">
            Reason (required)
            <textarea
              id="memberPermissionOverrideReason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="member-permission-override-reason"
              rows={2}
            />
          </label>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-outline"
            data-testid="member-permission-override-close"
            onClick={onClose}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={savePermissions}
            disabled={!canSave}
            data-testid="member-permission-override-save"
          >
            {saving ? 'Saving...' : 'Save overrides'}
          </button>
        </div>
      </div>

      <StepUpDialog
        challenge={stepUpChallenge}
        title="Permission change needs verification"
        description="Complete a fresh two-step verification before this permission override can be saved."
        onComplete={completeStepUp}
        onCancel={cancelStepUp}
      />
    </div>
  );
};

export default MemberPermissionOverridePanel;
